import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldAlert, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { apiClient } from "@/api/client";

function useRetentionDue() {
  return useQuery({
    queryKey: ["gdpr", "due"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/gdpr/applications/due");
      if (error || !data) throw new Error("Failed to load the retention list");
      return data;
    },
  });
}

/** Journey 12.2: dossiers past their retention term — deliberate, confirmed wipe. */
export function RetentionCard() {
  const qc = useQueryClient();
  const { data: due } = useRetentionDue();
  const [confirming, setConfirming] = useState(false);
  const anonymize = useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/gdpr/applications/anonymize",
        { body: { application_ids: ids } },
      );
      if (error || !data) throw new Error("Could not anonymise");
      return data;
    },
    onSuccess: (result) => {
      toast.success(`${result.anonymized} dossier(s) anonymised`);
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ["gdpr"] });
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
    onError: (err) => toast.error(err.message),
  });

  if (!due || due.length === 0) return null;

  return (
    <Card className="border-amber-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="size-4 text-amber-600" /> Data retention —{" "}
          {due.length} dossier(s) due
        </CardTitle>
        {confirming ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Removes name, contact details and all uploaded documents. This cannot be
              undone.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirming(false)}
              disabled={anonymize.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={anonymize.isPending}
              onClick={() => anonymize.mutate(due.map((d) => d.id))}
            >
              {anonymize.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}
              Confirm anonymisation
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
            <Trash2 className="size-4" />
            Anonymise all ({due.length})
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-6 py-3 font-medium">Candidate</th>
              <th className="px-6 py-3 font-medium">Property</th>
              <th className="px-6 py-3 font-medium">Closed</th>
              <th className="px-6 py-3 font-medium">Due since</th>
            </tr>
          </thead>
          <tbody>
            {due.map((row) => (
              <tr key={row.id} className="border-b border-border/60 last:border-0">
                <td className="px-6 py-3">
                  <span className="font-medium">{row.name}</span>{" "}
                  <span className="text-xs text-muted-foreground">{row.email}</span>
                </td>
                <td className="px-6 py-3 text-muted-foreground">
                  {[row.property_address, row.city].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="px-6 py-3">
                  {new Date(row.closed_at).toLocaleDateString("en-GB")}
                </td>
                <td className="px-6 py-3">
                  {new Date(row.due_since).toLocaleDateString("en-GB")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
